import {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {Button as BootstrapButton, Card, Form} from 'react-bootstrap';
import {DataSet, Network} from 'vis-network/standalone/esm/vis-network.min.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Button from '../../../../../Components/Button';
import {useGetPbxGroupes, useGetVirtualPbxNumeros} from '../../../Hooks/TelephonieManager';
import {
  ID_PLAGE_HORS_PLAGE,
  ID_PLAGE_VACANCES,
  NumeroGlobal,
  PbxGroupeAppel,
} from '../TrunkSip/mocks';
import {DEVIER_VERS_OPTIONS} from './Groupes';
import Loading from '../../../../../app/common/components/Loading';

/**
 * MAQUETTE — reproduction du « Call Flow Visualizer » : graphe interactif
 * numéro → plage horaire → groupe → destinations de débordement, avec
 * détection de boucle (comme l'algorithme récursif du legacy) et export PDF.
 */

const COULEUR_HEAD = '#1071b9';
const COULEUR_VERT = '#22c55e';
const COULEUR_VERT_SUB = '#86efac';
const COULEUR_ORANGE = '#f59e0b';
const COULEUR_ORANGE_SUB = '#fcd34d';
const COULEUR_ROUGE = '#ef4444';
const COULEUR_ROUGE_SUB = '#fca5a5';

type TypeNoeud = 'numero' | 'plage' | 'groupe' | 'destination' | 'overflow';

interface GrapheNode {
  id: string;
  label: string;
  color: string;
  type: TypeNoeud;
}

interface GrapheEdge {
  from: string;
  to: string;
  label?: string;
}

const construireGraphe = (
  numero: NumeroGlobal,
  groupesAppel: PbxGroupeAppel[],
  labelBoucle: string,
  resoudreLabel: (labelId: string) => string
): {nodes: GrapheNode[]; edges: GrapheEdge[]} => {
  const nodes: GrapheNode[] = [];
  const edges: GrapheEdge[] = [];
  const idsVus = new Set<string>();

  const idNumero = `numero-${numero.id}`;
  nodes.push({id: idNumero, label: numero.numeroFormate, color: COULEUR_HEAD, type: 'numero'});
  idsVus.add(idNumero);

  numero.plages.forEach((plage, index) => {
    const idPlage = `plage-${numero.id}-${index}`;
    const couleurPlage =
      plage.idPlage === ID_PLAGE_VACANCES
        ? COULEUR_ORANGE
        : plage.idPlage === ID_PLAGE_HORS_PLAGE
          ? COULEUR_ROUGE
          : COULEUR_VERT;
    const couleurSub =
      plage.idPlage === ID_PLAGE_VACANCES
        ? COULEUR_ORANGE_SUB
        : plage.idPlage === ID_PLAGE_HORS_PLAGE
          ? COULEUR_ROUGE_SUB
          : COULEUR_VERT_SUB;

    nodes.push({id: idPlage, label: plage.nomPlage, color: couleurPlage, type: 'plage'});
    edges.push({from: idNumero, to: idPlage});

    const idDestination = `destination-${plage.nomGroupe}`;
    if (idsVus.has(idDestination)) {
      edges.push({from: idPlage, to: idDestination, label: labelBoucle});
      return;
    }
    idsVus.add(idDestination);

    const groupeReel = groupesAppel.find((groupe) => groupe.nom === plage.nomGroupe);
    nodes.push({
      id: idDestination,
      label: plage.nomGroupe,
      color: couleurSub,
      type: groupeReel ? 'groupe' : 'destination',
    });
    edges.push({from: idPlage, to: idDestination});

    if (groupeReel) {
      [groupeReel.condNbMaxAppelants, groupeReel.condDureeAttenteMax, groupeReel.condDureeSonnerieMax].forEach(
        (regle, i) => {
          const idOverflow = `overflow-${idDestination}-${i}`;
          if (idsVus.has(idOverflow)) {
            return;
          }
          idsVus.add(idOverflow);
          const labelId = DEVIER_VERS_OPTIONS.find((option) => option.value === regle.devierVers)?.labelId;
          const label = labelId ? resoudreLabel(labelId) : regle.devierVers;
          nodes.push({id: idOverflow, label, color: couleurSub, type: 'overflow'});
          edges.push({from: idDestination, to: idOverflow});
        }
      );
    }
  });

  return {nodes, edges};
};

type ModeRendu = 'hierarchique_descendant' | 'hierarchique_centre' | 'aleatoire';

const optionsVisNetwork = (mode: ModeRendu) => ({
  autoResize: true,
  nodes: {
    shape: 'box',
    margin: {top: 10, right: 10, bottom: 10, left: 10},
    font: {size: 13, color: '#1a1a1a'},
    borderWidth: 1,
  },
  edges: {
    arrows: 'to',
    color: {color: '#A5ACB2'},
    smooth: {enabled: true, type: 'cubicBezier', roundness: 0.4},
  },
  interaction: {hover: true},
  physics: mode === 'aleatoire',
  layout:
    mode === 'aleatoire'
      ? {}
      : {
          hierarchical: {
            direction: 'UD',
            sortMethod: mode === 'hierarchique_centre' ? 'hubsize' : 'directed',
            levelSeparation: 110,
            nodeSpacing: 160,
          },
        },
});

interface CallFlowVisualizerProps {
  onFermer: () => void;
  onNaviguerVers: (onglet: 'numeros' | 'groupes' | 'plages') => void;
}

const CallFlowVisualizer = ({onFermer, onNaviguerVers}: CallFlowVisualizerProps): JSX.Element | null => {
  const intl = useIntl();
  const conteneurRef = useRef<HTMLDivElement>(null);
  const {data: numerosData, isLoading: isLoadingNumeros} = useGetVirtualPbxNumeros();
  const {data: groupesData, isLoading: isLoadingGroupes} = useGetPbxGroupes();
  const [headnumberSelectionne, setHeadnumberSelectionne] = useState<string | null>(null);
  const [modeRendu, setModeRendu] = useState<ModeRendu>('hierarchique_descendant');
  const [cleRefresh, setCleRefresh] = useState(0);
  const [exportEnCours, setExportEnCours] = useState(false);

  useEffect(() => {
    if (numerosData && numerosData.numerosGlobaux.length > 0 && headnumberSelectionne == null) {
      setHeadnumberSelectionne(numerosData.numerosGlobaux[0].numero);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numerosData]);

  const numeroActuel =
    numerosData?.numerosGlobaux.find((numero) => numero.numero === headnumberSelectionne) ?? null;

  useEffect(() => {
    if (!conteneurRef.current || !numeroActuel || !groupesData) {
      return;
    }
    const {nodes, edges} = construireGraphe(
      numeroActuel,
      groupesData.groupes,
      intl.formatMessage({id: 'TELEPHONIE.PBX.CALLFLOW.LOOP_DETECTED'}),
      (labelId) => intl.formatMessage({id: labelId})
    );
    const network = new Network(
      conteneurRef.current,
      {
        nodes: new DataSet(nodes),
        edges: new DataSet(edges),
      },
      optionsVisNetwork(modeRendu)
    );

    network.on('doubleClick', (params: {nodes: string[]}) => {
      if (params.nodes.length === 0) {
        return;
      }
      const node = nodes.find((candidate) => candidate.id === params.nodes[0]);
      if (!node) {
        return;
      }
      if (node.type === 'numero') {
        onNaviguerVers('numeros');
      } else if (node.type === 'groupe') {
        onNaviguerVers('groupes');
      } else if (node.type === 'plage') {
        onNaviguerVers('plages');
      }
    });

    return () => network.destroy();
  }, [numeroActuel, groupesData, modeRendu, cleRefresh, intl, onNaviguerVers]);

  if (isLoadingNumeros || isLoadingGroupes || !numerosData || !groupesData) {
    return <Loading className="col-md-6" />;
  }

  const exporterPdf = async (): Promise<void> => {
    if (!conteneurRef.current) {
      return;
    }
    setExportEnCours(true);
    try {
      const canvas = await html2canvas(conteneurRef.current);
      const orientation = canvas.width >= canvas.height ? 'landscape' : 'portrait';
      const pdf = new jsPDF({orientation});
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = (canvas.height * pageWidth) / canvas.width;
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pageWidth, pageHeight);
      pdf.save('YourVPBXConfiguration.pdf');
    } finally {
      setExportEnCours(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-5">
        <h3 className="fw-bold text-uppercase m-0">
          {intl.formatMessage({id: 'TELEPHONIE.PBX.CALLFLOW.TITLE'})}
        </h3>
        <Button
          label={intl.formatMessage({id: 'COMMON.BACK'})}
          faIcon="bi bi-arrow-left fs-6 me-2"
          onClick={onFermer}
        />
      </div>

      <p className="fs-6 mb-5">{intl.formatMessage({id: 'TELEPHONIE.PBX.CALLFLOW.INTRO'})}</p>

      <Card bg="light" className="mb-5">
        <Card.Body>
          <div className="fw-bold fs-6 mb-2">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.PLAGE_ACTUELLE'})}
          </div>
          <Form.Select
            style={{maxWidth: 320}}
            value={headnumberSelectionne ?? ''}
            onChange={(e) => setHeadnumberSelectionne(e.target.value)}
          >
            {numerosData.numerosGlobaux.map((numero) => (
              <option key={numero.id} value={numero.numero}>
                {numero.numeroFormate} ({numero.nom})
              </option>
            ))}
          </Form.Select>
        </Card.Body>
      </Card>

      <Card bg="light" className="mb-5">
        <Card.Body>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-4">
            <div className="d-flex flex-wrap gap-4">
              <Form.Check
                type="radio"
                id="mode-hierarchique-descendant"
                name="mode-rendu"
                label={intl.formatMessage({id: 'TELEPHONIE.PBX.CALLFLOW.MODE.HIERARCHIQUE_DESCENDANT'})}
                checked={modeRendu === 'hierarchique_descendant'}
                onChange={() => setModeRendu('hierarchique_descendant')}
              />
              <Form.Check
                type="radio"
                id="mode-hierarchique-centre"
                name="mode-rendu"
                label={intl.formatMessage({id: 'TELEPHONIE.PBX.CALLFLOW.MODE.HIERARCHIQUE_CENTRE'})}
                checked={modeRendu === 'hierarchique_centre'}
                onChange={() => setModeRendu('hierarchique_centre')}
              />
              <Form.Check
                type="radio"
                id="mode-aleatoire"
                name="mode-rendu"
                label={intl.formatMessage({id: 'TELEPHONIE.PBX.CALLFLOW.MODE.ALEATOIRE'})}
                checked={modeRendu === 'aleatoire'}
                onChange={() => setModeRendu('aleatoire')}
              />
              <i
                className="bi bi-arrow-clockwise text-primary fs-4 cursor-pointer"
                title={intl.formatMessage({id: 'TELEPHONIE.PBX.CALLFLOW.RAFRAICHIR'})}
                onClick={() => setCleRefresh((precedent) => precedent + 1)}
              />
            </div>
            <BootstrapButton variant="outline-primary" disabled={exportEnCours} onClick={exporterPdf}>
              <i className="bi bi-file-pdf me-2" />
              {intl.formatMessage({id: 'TELEPHONIE.PBX.CALLFLOW.EXPORT_PDF'})}
            </BootstrapButton>
          </div>
        </Card.Body>
      </Card>

      <div className="d-flex align-items-center gap-3 bg-primary text-white rounded p-3 mb-4">
        <i className="bi bi-info-circle-fill fs-4 flex-shrink-0" />
        <span className="fs-7">{intl.formatMessage({id: 'TELEPHONIE.PBX.CALLFLOW.INFO'})}</span>
      </div>

      <div
        ref={conteneurRef}
        style={{height: 520, border: '1px solid #A5ACB2', borderRadius: 4, background: '#fff'}}
      />
    </div>
  );
};

export default CallFlowVisualizer;
